import { useQuery } from '@tanstack/react-query';
import type { DashboardDto } from '@vmt/shared';
import { Plus, Radio, Trophy } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { Highlights } from '../components/Highlights';
import { LeaderboardTable } from '../components/LeaderboardTable';
import { SeriesCard } from '../components/SeriesCard';
import { Button } from '../components/ui/Button';
import { Card, CardHeader } from '../components/ui/Card';
import { EmptyState, ErrorState } from '../components/ui/EmptyState';
import { FullPageSpinner } from '../components/ui/Spinner';
import { api } from '../lib/api';
import { qk } from '../lib/query-keys';

export function DashboardPage() {
  const { can } = useAuth();
  const dashboard = useQuery({
    queryKey: qk.dashboard,
    queryFn: () => api<DashboardDto>('/stats/dashboard'),
    refetchInterval: 30_000,
  });

  if (dashboard.isLoading) return <FullPageSpinner />;
  if (dashboard.isError)
    return <ErrorState error={dashboard.error} onRetry={() => void dashboard.refetch()} />;
  const { live, recent, leaderboard, highlights } = dashboard.data!;

  return (
    <div className="space-y-8">
      <section className="clip-corner relative overflow-hidden border border-ink-700/60 bg-gradient-to-br from-ink-850 via-ink-900 to-ink-950 px-5 py-8 sm:px-8">
        <div className="absolute -top-10 -right-10 size-48 rotate-12 bg-accent/10" aria-hidden />
        <p className="font-display text-sm font-bold tracking-[0.3em] text-accent uppercase">
          Custom Matches
        </p>
        <h1 className="mt-1 text-4xl leading-none sm:text-5xl">Valo Match Tracker</h1>
        <p className="mt-3 max-w-xl text-sm text-ink-300">
          Serien im VCT-Stil, Live-Map-Veto und Statistiken für unsere Runde.
        </p>
        {can('organizer') && (
          <Link to="/series/new" className="mt-5 inline-block">
            <Button size="lg" icon={<Plus className="size-5" />}>
              Neue Serie
            </Button>
          </Link>
        )}
      </section>

      <section>
        <h2 className="mb-3 flex items-center gap-2 text-xl">
          <Radio className="size-5 text-accent" /> Laufende Serien
        </h2>
        {live.length ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {live.map((s) => (
              <SeriesCard key={s.id} series={s} />
            ))}
          </div>
        ) : (
          <Card>
            <EmptyState
              title="Gerade läuft nichts"
              description="Sobald ein Veto startet, erscheint die Serie hier."
            />
          </Card>
        )}
      </section>

      {highlights.length > 0 && (
        <section>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-xl">Highlights</h2>
            <Link to="/stats" className="text-sm text-ink-400 hover:text-bone">
              Alle Statistiken →
            </Link>
          </div>
          <Highlights items={highlights} />
        </section>
      )}

      <div className="grid gap-8 lg:grid-cols-[1.2fr_1fr]">
        <section>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-xl">Letzte Ergebnisse</h2>
            <Link to="/series" className="text-sm text-ink-400 hover:text-bone">
              Alle Serien →
            </Link>
          </div>
          {recent.length ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
              {recent.map((s) => (
                <SeriesCard key={s.id} series={s} />
              ))}
            </div>
          ) : (
            <Card>
              <EmptyState title="Noch keine Ergebnisse" />
            </Card>
          )}
        </section>
        <section>
          <Card>
            <CardHeader
              title={
                <span className="flex items-center gap-2">
                  <Trophy className="size-5 text-warning" /> Leaderboard
                </span>
              }
              action={
                <Link to="/stats" className="text-sm text-ink-400 hover:text-bone">
                  Mehr →
                </Link>
              }
            />
            {leaderboard.length ? (
              <LeaderboardTable entries={leaderboard} compact />
            ) : (
              <EmptyState title="Noch keine Statistiken" />
            )}
          </Card>
        </section>
      </div>
    </div>
  );
}
